import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNotes,
  exchangeTransactionsSelector,
  getExchangeTransactionByAccount,
  updateNotes,
} from '../../../store/exchanges/transactionsSlice';
import { getDateTimeFromTimestamp } from '../../../utilities/datetime';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../../utilities/money';
import AccountSvg from '../../../assets/accounts';
import { resolveAddress } from '../../../services/contactAndContract';
import { isEmpty } from '@firebase/util';
import toast, { Toaster } from 'react-hot-toast';
import WalletAddressEllipsis from '../../../components/shared/WalletAddressEllipsis';
import ProviderLogo from '../../../components/shared/ProviderLogo';
import CashSvg from '../../../assets/cash.svg';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';

const CustomDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      <p>{children ? getDateTimeFromTimestamp(children) : ''}</p>
    </DataTableCell>
  );
};

const CustomDataTableAmount = ({ children, ...props }) => {
  const { item } = props;
  const currency = item?.currency || '';
  const amount = getNumberWithCommas(children || 0, 4);
  return (
    <DataTableCell title={children} {...props}>
      <span className="slds-m-right_xx-small">
        <img src={CashSvg} alt=""></img>
      </span>
      {amount} {currency}
    </DataTableCell>
  );
};
CustomDataTableAmount.displayName = DataTableCell.displayName;

const CustomDataTableNotes = ({ children, ...props }) => {
  const [editedValue, setEditedValue] = useState(children ? children.note : '');
  const dispatch = useDispatch();

  const addUpdateNotesHandler = (values) => {
    dispatch(values.id ? updateNotes(values) : createNotes(values));
  };

  const onCellEditData = (item, data) => {
    props.setShowToast(true);
    // setEditedValue(item);
    // if (!item) return;
    const values = {
      note: {
        note: item,
        entity_id: data.item._id,
        type: 'transaction',
      },
      id: children ? children.id : null,
    };
    addUpdateNotesHandler(values);
  };

  return (
    <DataTableCell
      title={`${children.note ? children.note : 'Add note'}`}
      {...props}
    >
      <IconSettings iconPath="/assets/icons">
        <Input
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search',
              }}
              name="edit"
              category="utility"
            />
          }
          type="text"
          value={editedValue}
          placeholder="Add note"
          onChange={(e) => {
            setEditedValue(e.target.value);
          }}
          className={`editable-cell-input tablenote`}
          onBlur={(e) => onCellEditData(e.target.value, props)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onCellEditData(e.target.value, props);
            }
          }}
          styleInput={{border: '0px'}}
        />
      </IconSettings>
    </DataTableCell>
  );
};
CustomDataTableNotes.displayName = DataTableCell.displayName;

// Custom data table amount cell
const CustomDataTableAmountCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={`${children}`} {...props}>
      {item.amount && (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      )}

      {item.amount ? <>{`$${getNumbersWithDecimalPlaces(item.amount, 4)}`}</> : '--'}
    </DataTableCell>
  );
};
CustomDataTableAmountCell.displayName = DataTableCell.displayName;

// Custom data table gas cell
const CustomDataTableGasCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      {item.eth_gas_fee && item.usd_gas_fee ? (
        <>{`${getNumbersWithDecimalPlaces(item.eth_gas_fee, 4)} ETH ($${getNumbersWithDecimalPlaces(
          item.usd_gas_fee,
          2
        )})`}</>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};
CustomDataTableGasCell.displayName = DataTableCell.displayName;

const CustomDataTableCellOne = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      {children ? (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      ) : (
        '--'
      )}
      <WalletAddressEllipsis text={children.replace(/<[^>]*>?/gm, '')} maxTextCount={20} />
    </DataTableCell>
  );
};
CustomDataTableCellOne.displayName = DataTableCell.displayName;

CustomDataTableCell.displayName = DataTableCell.displayName;

const DataTableAccountColumn = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <Link
        // to={`/defi/${item.account}`}
        to="#"
      >
        <span className="slds-m-right_xx-small">
          <ProviderLogo provider={item?.account_provider} color={item?.account_color} />
        </span>
        {item.account_name ? item.account_name : children}
      </Link>
    </DataTableCell>
  );
};

DataTableAccountColumn.displayName = DataTableCell.displayName;

const DataTableTypeColumn = ({ children, ...props }) => {
  return (
    <DataTableCell title={children} {...props}>
      <div className="slds-m-right_xx-small" style={{ display: 'flex', gap: '5px' }}>
        <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        {children === 'deposit' ? 'Buy' : 'Sell'}
      </div>
    </DataTableCell>
  );
};

DataTableTypeColumn.displayName = DataTableCell.displayName;

const CustomDataTableChain = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <div className="slds-m-right_xx-small" style={{ display: 'flex', gap: '5px' }}>
        {item?.chain_logo && <img src={item.chain_logo} alt="" height="20" width="20"></img>}
        {item.chain_name}
      </div>
    </DataTableCell>
  );
};
CustomDataTableChain.displayName = DataTableCell.displayName;

const CustomDataTableName = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      <Link to={`/exchanges/transaction/${item._id}`}>
        <WalletAddressEllipsis text={children.replace(/<[^>]*>?/gm, '')} maxTextCount={20} />
      </Link>
    </DataTableCell>
  );
};
CustomDataTableName.displayName = DataTableCell.displayName;

const TransactionHistory = ({ account }) => {
  const dispatch = useDispatch();
  const { exchangeTransactionsHistory, loading, pagination, errors, success, message } =
    useSelector(exchangeTransactionsSelector);
  const [sortColumn, setSortColumn] = useState('');
  const [sortColumnDirection, setSortColumnDirection] = useState('');
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [paginated, setPaginated] = useState(0);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    dispatch(getExchangeTransactionByAccount({ account: account }));
  }, [account, dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (exchangeTransactionsHistory.length > 0) {
      setItems(exchangeTransactionsHistory);
    }
  }, [exchangeTransactionsHistory]);

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(true);
    setPaginated(0);
    dispatch(
      getExchangeTransactionByAccount({
        account: account,
        sort_column: property,
        sort_order: sortDirection,
      })
    );

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      setIsPageLoading(true);
      setTimeout(() => {
        if (pagination?.pages <= 1 || paginated >= pagination?.pages) {
          setHasMore(false);
          setIsPageLoading(false);
          return;
        }

        if (pagination.currentPage >= pagination.pages) {
          setHasMore(false);
          setIsPageLoading(false);
          return;
        }
        let url = new URLSearchParams(pagination.nextPageUrl);
        setPaginated(paginated + 1);
        dispatch(
          getExchangeTransactionByAccount({
            account,
            limit: url.get('limit'),
            offset: url.get('offset'),
            sort_column: sortColumn,
            sort_order: sortColumnDirection,
          })
        );
        setIsPageLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (!showToast) return;
    success &&
      !loading &&
      toast.success(message, {
        id: 'exchange-transaction_history-success',
      });

    !success &&
      !loading &&
      !isEmpty(errors) &&
      toast.error(errors, {
        id: 'exchange-transaction_history-error',
      });
  }, [success, message, showToast, errors, loading]);

  // if (success && showToast) {
  //   toast.success(message, {
  //     id: 'exchange-transaction_history-success',
  //   });
  //   setShowToast(false);
  // }

  // if (!isEmpty(errors) && showToast) {
  //   toast.error(errors, {
  //     id: 'exchange-transaction_history-error',
  //   });
  //   setShowToast(false);
  // }

  return (
    <div style={{display: 'flex'}}>
      <Toaster position="top-right" reverseOrder={false} />
      <div
        style={{
          alignSelf: 'flex-start',
          marginTop: '10px',
          height: `${exchangeTransactionsHistory.length === 0 ? '100%' : 'calc(100vh - 155px)'}`,
          width: '100%',
        }}
      >
        <DataTable
          assistiveText={{
            actionsHeader: 'actions',
            columnSort: 'sort this column',
            columnSortedAscending: 'asc',
            columnSortedDescending: 'desc',
            selectAllRows: 'Select all rows',
            selectRow: 'Select this row',
          }}
          fixedHeader
          fixedLayout
          items={items}
          id="TransactionTable"
          onSort={handleSort}
          hasMore={hasMore}
          className="defi-transaction-dataTable"
          onLoadMore={handleLoadMore}
          noRowHover
        >
          <DataTableColumn
            isSorted={sortColumn === 'txid'}
            label="Transaction ID"
            primaryColumn
            property="txid"
            sortable
            sortDirection={sortColumnDirection.txid}
            width="12rem"
          >
            <CustomDataTableName />
          </DataTableColumn>
          <DataTableColumn
            isSorted={sortColumn === 'address'}
            label="Address"
            property="address"
            sortable
            sortDirection={sortColumnDirection.address}
            width="12rem"
          >
            <CustomDataTableCellOne />
          </DataTableColumn>
          <DataTableColumn label="Type" property="type" width="10rem">
            <DataTableTypeColumn />
          </DataTableColumn>
          <DataTableColumn sortable isDefaultSortDescending label="Leverage" property="amount">
            <CustomDataTableAmount />
          </DataTableColumn>
          <DataTableColumn sortable isDefaultSortDescending label="Amount" property="amount">
            <CustomDataTableAmount />
          </DataTableColumn>
          <DataTableColumn label="Notes" property="note">
            <CustomDataTableNotes setShowToast={setShowToast} />
          </DataTableColumn>
          <DataTableColumn
            label="Transaction Date"
            property="createdAt"
            sortable
            sortDirection={sortColumnDirection.createdAt}
          >
            <CustomDataTableCell />
          </DataTableColumn>
        </DataTable>
        {exchangeTransactionsHistory.length === 0 && !hasMore && (
          <div className="nodatafound" style={{marginTop: '90px'}}>
            <div>
              <img src="/assets/empty-state/empty-folder.png" alt="" style={{ height: '150px' }} />
              <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                Data not available
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
